import { useEffect, useEffectEvent, useRef, useState } from "react";
import { Navigate } from "react-router";

import LoginForm from "@/features/login/components/login-form";
import McpConnectionFailed from "@/features/login/components/mcp-connection-failed";
import McpCallbackForm, { type McpCallbackHandle } from "@/features/login/mcp-callback-form";
import {
  completeMcpAuthCallbackForProvider,
  getMcpFailureMessage,
  isMcpFailureRetryable,
  MCP_CANCELLED_ERROR,
  redirectOnLapsedEntitlement,
} from "@/features/login/complete-mcp-auth-callback";
import { useMcpClientName } from "@/features/login/mcp-client-name";
import { useGetSoftwareStatement } from "@/features/software-statement/api/get-software-statement";
import { useSession } from "@/features/account/api/get-session";
import useQueryParams from "@/hooks/use-query-params";
import { getSafeRedirect } from "@/lib/utils";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import { paths } from "@/config/paths";
import { usePreserveQuery } from "@/hooks/use-preserve-query";

/** Where an already-signed-in visitor is sent when no safe `?r=` is supplied. */
const CONSOLE_HREF = "/console";

const LoginRoute = () => {
  const { data: session, isLoading } = useSession();
  const { redirect, isFromMCP, wantsSignIn, callbackUrl, state } = useQueryParams();
  const preserveQuery = usePreserveQuery();
  const { provider } = useMcpProvider();
  const providerRef = useRef(provider);
  providerRef.current = provider;
  const clientName = useMcpClientName(state, callbackUrl);
  const softwareStatementMutation = useGetSoftwareStatement();
  const callbackRef = useRef<McpCallbackHandle>(null);
  const mcpCallbackStartedRef = useRef(false);
  const [failure, setFailure] = useState<{ message: string; retryable: boolean } | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  // The previous failure stays on screen for the whole retry: it is the only thing rendered
  // on this path, so clearing it here would blank the page again and take the pending state
  // with it. A success navigates away; a new failure replaces the message below.
  const runMcpCallback = async () => {
    if (mcpCallbackStartedRef.current) return;
    mcpCallbackStartedRef.current = true;
    setIsRunning(true);

    try {
      await completeMcpAuthCallbackForProvider({
        getProvider: () => providerRef.current,
        fetchSoftwareStatement: input => softwareStatementMutation.mutateAsync(input),
        callbackRef,
      });
    } catch (error) {
      console.error(error);
      mcpCallbackStartedRef.current = false;
      setIsRunning(false);
      if (redirectOnLapsedEntitlement(error)) return;
      setFailure({ message: getMcpFailureMessage(error), retryable: isMcpFailureRetryable(error) });
    }
  };

  const completeExistingSessionMcpCallback = useEffectEvent(() => {
    void runMcpCallback();
  });

  useEffect(() => {
    // `session` is `undefined` while loading and `null` when anonymous; only act when signed in.
    if (!session) return;

    if (isFromMCP) {
      completeExistingSessionMcpCallback();
      return;
    }

    const target = getSafeRedirect(redirect) ?? CONSOLE_HREF;
    window.location.replace(target);
  }, [session, redirect, isFromMCP]);

  // Already signed in for a normal (non-MCP) visit: redirect is imminent.
  if (isLoading || (session && !isFromMCP)) {
    return null;
  }

  // Already signed in for MCP: POST the software statement to the auth callback. There is
  // no page here to fall back on, so a failure renders one rather than a toast over white.
  if (session && isFromMCP) {
    return (
      <>
        <McpCallbackForm handleRef={callbackRef} />
        {failure && (
          <McpConnectionFailed
            message={failure.message}
            clientName={clientName}
            onRetry={failure.retryable ? () => void runMcpCallback() : undefined}
            isRetrying={isRunning}
            cancelled={failure.message === MCP_CANCELLED_ERROR}
          />
        )}
      </>
    );
  }

  // Anonymous, and an MCP client sent them here: sign-up is the likelier need. Someone
  // adding the connector for the first time who guesses a password on this form only gets
  // "invalid credentials" — nothing points at the Sign up link, and the usual next stop is
  // a password reset for an account that does not exist. Landing on sign-up instead fails
  // softly: an email that already exists is recognised and sent back here.
  if (isFromMCP && !wantsSignIn) {
    return <Navigate to={preserveQuery(paths.register.getHref())} replace />;
  }

  return <LoginForm />;
};

export default LoginRoute;
